import { useCombobox } from "downshift";
import { Input, Box, Flex } from "@chakra-ui/react";
import React, { useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import { toTitle } from "@/utils/case";
import SVG from "react-inlinesvg";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  matchesSearchKeyword,
  normalizeSearchText,
} from "@/utils/search-text";

export type Suggestion = {
  label: string;
  type: "Brand" | "Boutique" | "SKU" | "custom"; // 타입 원하는 만큼 추가 가능
  value: string; // 실제 값, 예: 브랜드 ID, SKU 코드 등
  // label 말고도 이 항목을 찾을 수 있는 말들(브랜드 유사 표기 등). 검색에만 쓰고 목록에는 label 만 보인다.
  keywords?: string[];
};

interface Props {
  value?: Suggestion;
  width?: string;
  placeholder?: string;
  leadingIcon?: string;
  submit?: (item: Suggestion) => void;
  suggestions: Suggestion[];
  showButton?: boolean; // 버튼 표시 여부
}


export function AutoCompleteInput({ value, width, placeholder, leadingIcon, submit, suggestions, showButton }: Props) {
  const [inputItems, setInputItems] = useState(suggestions);
  const { t } = useI18nHelper();

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    inputValue,
    setInputValue,
  } = useCombobox({
    items: inputItems,
    itemToString: (item) => (item ? item.label : ""),  // ← 이게 핵심!
    onInputValueChange: ({ inputValue }) => {
      if (!inputValue || inputValue.length < 1) {
        setInputItems([]);
        return;
      }

      const KOREAN_KEYS: Record<string, string> = {
        'ㅂ': 'q','ㅈ': 'w','ㄷ': 'e','ㄱ': 'r','ㅅ': 't',
        'ㅛ': 'y','ㅕ': 'u','ㅑ': 'i','ㅐ': 'o','ㅔ': 'p',
        'ㅁ': 'a','ㄴ': 's','ㅇ': 'd','ㄹ': 'f','ㅎ': 'g',
        'ㅗ': 'h','ㅓ': 'j','ㅏ': 'k','ㅣ': 'l',
        'ㅋ': 'z','ㅌ': 'x','ㅊ': 'c','ㅍ': 'v','ㅠ': 'b',
        'ㅜ': 'n','ㅡ': 'm'
      };

      function koreanToEnglishKeyboard(text: string) {
        return [...text].map(c => KOREAN_KEYS[c] || c).join('');
      }


      const input = inputValue.toLowerCase();
      const converted = koreanToEnglishKeyboard(input).toLowerCase();

      // 상품 검색(isProductFiltered)은 -, _, 공백 같은 구분자를 무시하고 맞춘다.
      // 자동완성만 글자 그대로 비교하면 `816083XHAKB` 를 쳐도 `816083 XHAKB 1000` 이
      // 목록에 안 떠서 "자동완성이 안 된다"가 된다 — 같은 규칙으로 맞춘다.
      // 브랜드는 label(정식명) 외에 유사 표기(keywords)로도 찾게 한다 —
      // 'EMPORIO ARMANI' 나 '프라다' 로 쳐도 해당 브랜드가 떠야 한다.
      setInputItems(
        suggestions.filter((s) =>
          [s.label, ...(s.keywords || [])].some(
            (text) =>
              matchesSearchKeyword(text, input) ||
              matchesSearchKeyword(text, converted)
          )
        )
      );
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        submit?.(selectedItem);
      }
    },
  });

  // 초기값 설정 — 라벨이 실제로 바뀔 때만.
  // value 와 suggestions 는 부모가 렌더마다 새로 만들기 때문에 객체를 deps 로 두면
  // 부모가 리렌더될 때마다(수량 입력, 관심 표시 등) 치던 글자가 value.label 로 되돌아간다.
  const valueLabel = value?.label;
  React.useEffect(() => {
    if (valueLabel === undefined) return;
    setInputValue(valueLabel);
    setInputItems(
      suggestions.filter((s) => matchesSearchKeyword(s.label, valueLabel))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueLabel, setInputValue]);

  return (
    <Flex alignItems="center" position="relative">
      <Box position="relative" role="combobox" width={width}>
        <Input
          {...getInputProps({
            placeholder,
            tabIndex: 0,
            onKeyDown: (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const matched = suggestions.find(
                  (s) =>
                    normalizeSearchText(s.label) ===
                    normalizeSearchText(inputValue)
                );
                if (matched) {
                  submit?.(matched);
                } else {
                  if (highlightedIndex == -1) {
                    submit?.({ label: inputValue, type: "custom", value: inputValue });
                  }
                }
              }
            },
          })}
          padding={"0 8px"}
          margin={"0"}
          minHeight={"28px"}
          height={"28px"}
          fontSize={"inherit"}
          textAlign={"start"}
          paddingLeft={leadingIcon ? "26px" : "8px"}
          paddingRight="26px"
          border={"none"}
          _focus={{
            boxShadow: "none",
            border: "none",
          }}
          _hover={{
            border: "none",
          }}
        />
        { inputValue.length > 0 && <Box
          width="20px"
          height="20px"
          cursor="pointer"
          position="absolute"
          top="50%"
          right="6px"
          transform="translateY(-50%)"
          zIndex={10}
        >
          <SVG
            src="/icons-v1_1/x-circle-solid.svg"
            width={"20px"}
            height={"20px"}
            onClick={() => {
              if (inputValue) {
                setInputValue("");
              }
              submit?.({
                label: "",
                type: "custom",
                value: ""
              }); // 선택된 아이템 초기화
            }}
          />
        </Box>}
        <Box
          {...getMenuProps()}
          position="absolute"
          zIndex={10}
          bg="white"
          width="100%"
          maxHeight="200px"
          overflowY="auto"
          border={isOpen && inputItems.length > 0 && inputValue.length > 0 ? "1px solid #ccc" : ""}
          borderRadius="md"
          mt={1}
        >
          {isOpen && inputValue.length > 0 &&
            inputItems.map((item, index) => (
              <Box
                key={`${item.label}-${index}`}
                {...getItemProps({ item, index })}
                bg={highlightedIndex === index ? "gray.100" : "white"}
                px={3}
                py={2}
                cursor="pointer"
              >
                <Box><strong style={{ color: "#999" }}>{item.type}</strong></Box> {item.label}
              </Box>
            ))}
        </Box>
      </Box>
      {showButton && <AppButton
        type={"submit"}
        variant={"normal"}
        onClick={() => {
          const matched = suggestions.find(
            (s) =>
              normalizeSearchText(s.label) === normalizeSearchText(inputValue)
          );
          submit?.(matched ?? { label: inputValue, type: "custom", value: inputValue });
        }}
      >
        {toTitle(t("search"))}
      </AppButton> }
    </Flex>
  );
}
